import { useState } from "react";

import { useDeactivateWorkbenchApiKey } from "@vega-api/generated/api-gateway-workbench-v1/api-gateway-workbench-v1";
import { type ApiGatewayWorkbenchApiKey } from "@vega-api/generated/types/apiGatewayWorkbenchApiKey";
import { InputControl } from "@vega-core/components/Form/Controls/Input/InputControl";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { Notice } from "@vega-core/components/Notice/Notice";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";

const CONFIRM_TEXT = "DEACTIVATE";

type DeactivateKeyDialogProps = {
  apiKey: ApiGatewayWorkbenchApiKey;
  onClose: VoidFunction;
  onDeactivated: VoidFunction;
};

export const DeactivateKeyDialog = ({
  apiKey,
  onClose,
  onDeactivated,
}: DeactivateKeyDialogProps) => {
  const [confirmInput, setConfirmInput] = useState("");
  const showToast = useToast();
  const { mutate: deactivateKey, isPending } = useDeactivateWorkbenchApiKey({
    mutation: {
      onSuccess: () => {
        showToast({
          title: m.settings_reportingApiKeys_toast_deactivated(),
          variant: "success",
        });
        onDeactivated();
        onClose();
      },
      onError: () => {
        showToast({
          title: m.settings_reportingApiKeys_toast_error(),
          variant: "destructive",
        });
      },
    },
  });

  return (
    <ActionaryModal
      ModalButtonsGroupProps={{
        mainActionText: m.settings_reportingApiKeys_deactivate_confirm(),
        mainActionVariant: "destructive",
        mainAction: () =>
          deactivateKey({ userId: apiKey.userId, keyName: apiKey.keyName }),
        disabled: confirmInput !== CONFIRM_TEXT,
        isLoading: isPending,
        secondaryActionText: m.btn_cancel(),
        secondaryAction: onClose,
      }}
      dataTestId="reporting-api-keys-deactivate-dialog"
      open
      setOpen={(value) => {
        if (!value) onClose();
      }}
      title={
        <div className="flex flex-col gap-1">
          <span>{m.settings_reportingApiKeys_deactivate_title()}</span>
          <span className="body-base-regular text-text/secondary break-all">
            {apiKey.keyName}
          </span>
        </div>
      }
    >
      <div className="flex flex-col gap-4 text-left">
        <Notice
          content={m.settings_reportingApiKeys_deactivate_warning()}
          title={m.settings_reportingApiKeys_noGracePeriod()}
          type="warning"
          variant="accent"
        />
        <InputControl
          label={m.settings_reportingApiKeys_deactivate_typeToConfirm()}
          name="reporting-api-keys-deactivate-confirm"
          onChange={(event) => setConfirmInput(event.target.value)}
          placeholder={CONFIRM_TEXT}
          type="text"
          value={confirmInput}
          withBorder
        />
      </div>
    </ActionaryModal>
  );
};
